// src/app/features/admin/store/admin.state.ts

import { UserListItem, UserDetail, Branch, UserListFilter } from '../models/admin.model';

// ── Re-usable generics ──────────────────────────────────────────────

export interface CacheEntry<T> {
  data: T;
  loadedAt: number;
  ttlMs: number;
}

export function isCacheValid<T>(entry: CacheEntry<T> | null | undefined): boolean {
  if (!entry) return false;
  return Date.now() - entry.loadedAt < entry.ttlMs;
}

export interface AsyncSlice<T> {
  data: T | null;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  cache: CacheEntry<T> | null;
}

export function emptySlice<T>(): AsyncSlice<T> {
  return { data: null, loading: false, loaded: false, error: null, cache: null };
}

// ── TTL constants ───────────────────────────────────────────────────

export const ADMIN_TTL = {
  USERS:  2 * 60 * 1000,   // 2 min  — user list (re-fetched on filter change)
  STATIC: 30 * 60 * 1000,  // 30 min — branches
  DETAIL: 5 * 60 * 1000,   // 5 min  — user detail
} as const;

// ── State shape ─────────────────────────────────────────────────────

export interface AdminState {
  users: AsyncSlice<UserListItem[]>;
  lastFilter: UserListFilter | null;
  branches: AsyncSlice<Branch[]>;
  userDetail: Record<number, AsyncSlice<UserDetail>>;
}

export const initialAdminState: AdminState = {
  users: emptySlice(),
  lastFilter: null,
  branches: emptySlice(),
  userDetail: {},
};
